import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { LoadingPanel } from '../../components/ui/Loader';
import { Checkbox, Textarea } from '../../components/ui/form';
import { ApiError, api } from '../../lib/api';
import { PresetGlyph } from '../mastering/PresetGlyph';

interface Band {
  threshold_db: number;
  ratio: number;
  attack_ms: number;
  release_ms: number;
  makeup_db: number;
}

interface Chain {
  eq_curve: [number, number][];
  user_eq: { low_db: number; mid_db: number; high_db: number };
  multiband: { splits_hz: number[]; bands: Band[] };
  saturation: { amount: number; drive: number; blend: number };
  width: { side_gain_db: number; bass_mono_hz: number };
  loudness: { target_lufs: number; true_peak_dbtp: number; lra: number };
  bypass: { multiband: boolean; saturation: boolean; width: boolean };
}

interface Preset {
  slug: string;
  name: string;
  blurb: string;
  icon: string;
  chain: Chain;
  version: number;
  enabled: boolean;
  sort: number;
  updated_at: string;
}

const ICONS = ['notes', 'flame', 'gem', 'cassette', 'leaf', 'orbit', 'film', 'burst'];

/**
 * Tuning the eight tiles.
 *
 * Every number the engine takes, editable, with the engine's own ranges
 * enforced on save (`Chain::validate`), so a bad value is refused here
 * with its field named and never reaches a render. Saving bumps the
 * tile's version, which every later render carries as `chain_version`;
 * renders made under the old numbers are visibly from before, and the
 * engine's cache misses.
 *
 * What a preset *sounds* like is settled by ear in IR8's playground,
 * then written here. This page is the numbers, not the listening.
 */
export function AdminMasteringPage() {
  const query = useQuery({
    queryKey: ['admin', 'mastering', 'presets'],
    queryFn: () => api.get<{ presets: Preset[]; neutral: Chain }>('/admin/mastering/presets'),
  });
  const [open, setOpen] = useState<string | null>(null);

  if (query.isPending) return <LoadingPanel />;

  if (query.isError || !query.data) return <Alert tone="error">Could not load the presets.</Alert>;

  return (
    <div className="flex flex-col gap-4" data-admin-mastering>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Mastering</h1>
        <p className="mt-1 max-w-2xl text-sm text-soft">
          The eight tiles, as the numbers the engine takes. Settle a sound in the IR8 playground, then write
          it here. Saving bumps the version; every render says which version it was made with.
        </p>
      </div>

      <ul className="flex flex-col gap-2">
        {query.data.presets.map((preset) => (
          <li key={preset.slug}>
            <Card className="p-4">
              <button
                type="button"
                className="flex w-full items-center gap-3 text-left"
                onClick={() => setOpen(open === preset.slug ? null : preset.slug)}
                data-preset-row={preset.slug}
              >
                <PresetGlyph icon={preset.icon} className="h-6 w-6" />
                <span className="flex-1">
                  <span className="text-sm font-medium text-ink">{preset.name}</span>
                  <span className="ml-2 text-xs text-faint">{preset.blurb}</span>
                </span>
                {!preset.enabled && <Chip>off</Chip>}
                <span className="font-mono text-xs text-faint">
                  v{preset.version} · {preset.chain.loudness.target_lufs} LUFS
                </span>
              </button>

              {open === preset.slug && <PresetEditor preset={preset} neutral={query.data.neutral} />}
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PresetEditor({ preset, neutral }: { preset: Preset; neutral: Chain }) {
  const client = useQueryClient();
  const [meta, setMeta] = useState({ name: preset.name, blurb: preset.blurb, icon: preset.icon, enabled: preset.enabled, sort: preset.sort });
  const [chain, setChain] = useState<Chain>(preset.chain);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setChain(preset.chain);
    setMeta({ name: preset.name, blurb: preset.blurb, icon: preset.icon, enabled: preset.enabled, sort: preset.sort });
  }, [preset]);

  const save = useMutation({
    mutationFn: () => api.put<Preset>(`/admin/mastering/presets/${preset.slug}`, { ...meta, chain }),
    onSuccess: (saved) => {
      setErrors({});
      setMessage(`Saved as v${saved.version}.`);
      void client.invalidateQueries({ queryKey: ['admin', 'mastering'] });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        setMessage(Object.keys(error.fieldErrors).length ? null : error.message);
      } else {
        setMessage('That could not be saved.');
      }
    },
  });

  const num = (path: string, value: number, apply: (c: Chain, v: number) => void) => (
    <NumberField
      label={path.split('.').pop() ?? path}
      value={value}
      error={errors[path]}
      onChange={(v) => setChain((c) => {
        const next = structuredClone(c);
        apply(next, v);
        return next;
      })}
    />
  );

  return (
    <div className="mt-4 flex flex-col gap-5 border-t border-line pt-4" data-preset-editor={preset.slug}>
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Name" value={meta.name} maxLength={40} onChange={(e) => setMeta({ ...meta, name: e.target.value })} error={errors.name} />
        <Field label="Blurb" value={meta.blurb} maxLength={160} onChange={(e) => setMeta({ ...meta, blurb: e.target.value })} error={errors.blurb} className="sm:col-span-2" />
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">Icon</span>
          <select
            value={meta.icon}
            onChange={(e) => setMeta({ ...meta, icon: e.target.value })}
            className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink"
          >
            {ICONS.map((icon) => (
              <option key={icon} value={icon}>
                {icon}
              </option>
            ))}
          </select>
        </label>
        <Field label="Order" type="number" value={String(meta.sort)} onChange={(e) => setMeta({ ...meta, sort: Number(e.target.value) })} />
        <div className="flex items-end pb-2">
          <Checkbox label="Shown to producers" checked={meta.enabled} onChange={(e) => setMeta({ ...meta, enabled: e.target.checked })} />
        </div>
      </div>

      <Section title="EQ curve" hint="Gain in dB at each frequency. The engine interpolates over log frequency between points.">
        <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
          {chain.eq_curve.map(([hz, gain], i) => (
            <NumberField
              key={hz}
              label={`${hz >= 1000 ? `${hz / 1000}k` : hz}`}
              value={gain}
              step={0.5}
              error={errors[`eq_curve[${i}][1]`]}
              onChange={(v) =>
                setChain((c) => {
                  const next = structuredClone(c);
                  next.eq_curve[i] = [hz, v];
                  return next;
                })
              }
            />
          ))}
        </div>
      </Section>

      <Section title="Loudness" hint="Target in LUFS, the limiter's ceiling in dBTP, and the loudness range above which the engine compresses to fit.">
        <div className="grid grid-cols-3 gap-3">
          {num('loudness.target_lufs', chain.loudness.target_lufs, (c, v) => { c.loudness.target_lufs = v; })}
          {num('loudness.true_peak_dbtp', chain.loudness.true_peak_dbtp, (c, v) => { c.loudness.true_peak_dbtp = v; })}
          {num('loudness.lra', chain.loudness.lra, (c, v) => { c.loudness.lra = v; })}
        </div>
      </Section>

      <Section title="Multiband" hint={`Splits at ${chain.multiband.splits_hz.join(' and ')} Hz. Ratio and threshold scale with intensity; attack and release do not.`}>
        <div className="grid gap-2">
          <div className="grid grid-cols-3 gap-3">
            {chain.multiband.splits_hz.map((hz, i) => (
              <NumberField
                key={i}
                label={`split ${i + 1} (Hz)`}
                value={hz}
                step={10}
                error={errors[`multiband.splits_hz[${i}]`]}
                onChange={(v) =>
                  setChain((c) => {
                    const next = structuredClone(c);
                    next.multiband.splits_hz[i] = v;
                    return next;
                  })
                }
              />
            ))}
          </div>
          {chain.multiband.bands.map((band, i) => (
            <div key={i} className="grid grid-cols-5 gap-2">
              {(['threshold_db', 'ratio', 'attack_ms', 'release_ms', 'makeup_db'] as const).map((key) => (
                <NumberField
                  key={key}
                  label={`${['low', 'mid', 'high', 'air'][i] ?? i} ${key.replace('_', ' ')}`}
                  value={band[key]}
                  step={key === 'ratio' ? 0.1 : 1}
                  error={errors[`multiband.bands[${i}].${key}`]}
                  onChange={(v) =>
                    setChain((c) => {
                      const next = structuredClone(c);
                      next.multiband.bands[i]![key] = v;
                      return next;
                    })
                  }
                />
              ))}
            </div>
          ))}
        </div>
      </Section>

      <div className="grid gap-4 sm:grid-cols-2">
        <Section title="Saturation" hint="Amount scales with intensity; drive and blend do not. Amount 0 skips the stage.">
          <div className="grid grid-cols-3 gap-3">
            {num('saturation.amount', chain.saturation.amount, (c, v) => { c.saturation.amount = v; })}
            {num('saturation.drive', chain.saturation.drive, (c, v) => { c.saturation.drive = v; })}
            {num('saturation.blend', chain.saturation.blend, (c, v) => { c.saturation.blend = v; })}
          </div>
        </Section>
        <Section title="Width" hint="Side gain applies above the bass-mono split; everything below is summed to mono.">
          <div className="grid grid-cols-2 gap-3">
            {num('width.side_gain_db', chain.width.side_gain_db, (c, v) => { c.width.side_gain_db = v; })}
            {num('width.bass_mono_hz', chain.width.bass_mono_hz, (c, v) => { c.width.bass_mono_hz = v; })}
          </div>
        </Section>
      </div>

      <Section title="Bypass">
        <div className="flex flex-wrap gap-4">
          {(['multiband', 'saturation', 'width'] as const).map((stage) => (
            <Checkbox
              key={stage}
              label={`Skip ${stage}`}
              checked={chain.bypass[stage]}
              onChange={(e) =>
                setChain((c) => ({ ...c, bypass: { ...c.bypass, [stage]: e.target.checked } }))
              }
            />
          ))}
        </div>
      </Section>

      <details className="text-xs text-faint">
        <summary className="cursor-pointer">The chain as JSON</summary>
        <Textarea
          className="mt-2 min-h-40 font-mono text-[11px]"
          value={JSON.stringify(chain, null, 2)}
          onChange={(e) => {
            try {
              setChain(JSON.parse(e.target.value) as Chain);
              setMessage(null);
            } catch {
              setMessage('That is not valid JSON yet.');
            }
          }}
        />
      </details>

      <div className="flex flex-wrap items-center gap-3">
        <Button loading={save.isPending} onClick={() => save.mutate()} data-save-preset>
          Save as v{preset.version + 1}
        </Button>
        <Button variant="ghost" onClick={() => setChain(neutral)}>
          Start from neutral
        </Button>
        {message && <span className={`text-sm ${Object.keys(errors).length ? 'text-hot-text' : 'text-faint'}`}>{message}</span>}
        {Object.keys(errors).length > 0 && !message && <span className="text-sm text-hot-text">Some numbers are out of range.</span>}
      </div>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <div>
        <h3 className="text-sm font-medium text-ink">{title}</h3>
        {hint && <p className="text-xs text-faint">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function NumberField({ label, value, step = 0.1, error, onChange }: { label: string; value: number; step?: number; error?: string; onChange: (v: number) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="truncate text-[11px] text-faint" title={label}>
        {label}
      </span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-invalid={error ? true : undefined}
        className={`rounded-[var(--radius-control)] border bg-app px-2 py-1.5 font-mono text-xs text-ink ${error ? 'border-hot-edge' : 'border-line'}`}
      />
      {error && <span className="text-[10px] text-hot-text">{error}</span>}
    </label>
  );
}
